// Time Rounding Utility Functions

import { getCompanySettings, updateCompanySettings } from './companyMigration';

// Round timestamp to nearest quarter hour (15 minutes) in Chicago timezone
export const roundTimestampToQuarterHour = (timestamp) => {
  const original = new Date(timestamp);

  // Extract Chicago local hour/minute for the original timestamp
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(original);

  const hours = parseInt(parts.find(p => p.type === 'hour').value, 10);
  const minutes = parseInt(parts.find(p => p.type === 'minute').value, 10);

  const chicagoTotalMinutes = (hours * 60) + minutes;
  const roundedTotalMinutes = Math.round(chicagoTotalMinutes / 15) * 15;
  const deltaMinutes = roundedTotalMinutes - chicagoTotalMinutes;

  // Apply the delta to the original UTC timestamp to avoid TZ reconstruction bugs
  const rounded = new Date(original.getTime() + (deltaMinutes * 60 * 1000));
  rounded.setSeconds(0, 0);
  return rounded.toISOString();
};

// Ceil timestamp up to next quarter hour (15 minutes) in Chicago timezone
export const ceilTimestampToQuarterHour = (timestamp) => {
  const original = new Date(timestamp);

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(original);

  const hours = parseInt(parts.find(p => p.type === 'hour').value, 10);
  const minutes = parseInt(parts.find(p => p.type === 'minute').value, 10);

  const chicagoTotalMinutes = (hours * 60) + minutes;
  const ceiledTotalMinutes = Math.ceil(chicagoTotalMinutes / 15) * 15;
  const deltaMinutes = ceiledTotalMinutes - chicagoTotalMinutes;

  const ceiled = new Date(original.getTime() + (deltaMinutes * 60 * 1000));
  ceiled.setSeconds(0, 0);
  return ceiled.toISOString();
};

// Helper function to get Chicago timezone offset
const getChicagoOffset = (date) => {
  // Create a date in UTC and in Chicago time
  const utcDate = new Date(date.toISOString());
  const chicagoDateString = date.toLocaleString('en-US', { timeZone: 'America/Chicago' });
  const chicagoAsUTC = new Date(chicagoDateString + ' UTC');

  // The difference is the offset
  return chicagoAsUTC.getTime() - utcDate.getTime();
};

// Floor timestamp to previous quarter hour (never rounds into the future)
export const floorTimestampToQuarterHour = (timestamp) => {
  const date = new Date(timestamp);
  const minutes = date.getMinutes();
  const flooredMinutes = Math.floor(minutes / 15) * 15;

  const flooredDate = new Date(date);
  flooredDate.setMinutes(flooredMinutes);
  flooredDate.setSeconds(0);
  flooredDate.setMilliseconds(0);

  return flooredDate.toISOString();
};

// Round time to nearest quarter hour (15 minutes)
export const roundToQuarterHour = (timeString) => {
  if (!timeString) return timeString;

  console.log('🔧 Rounding input:', timeString);

  // Parse time string (format: "HH:MM:SS AM/PM" or "HH:MM AM/PM")
  const timeParts = timeString.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
  if (!timeParts) {
    console.log('🔧 Failed to parse time:', timeString);
    return timeString;
  }

  let hours = parseInt(timeParts[1]);
  const minutes = parseInt(timeParts[2]);
  const isPM = timeParts[4] && timeParts[4].toUpperCase() === 'PM';

  // Convert to 24-hour format if needed
  if (isPM && hours !== 12) hours += 12;
  if (!isPM && hours === 12) hours = 0;

  // Calculate total minutes and round to nearest 15
  const totalMinutes = hours * 60 + minutes;
  const roundedMinutes = Math.round(totalMinutes / 15) * 15;

  console.log(`🔧 Original: ${hours}:${minutes.toString().padStart(2, '0')} (${totalMinutes} mins) -> Rounded: ${roundedMinutes} mins`);

  // Convert back to hours and minutes
  const newHours = Math.floor(roundedMinutes / 60) % 24;
  const newMinutes = roundedMinutes % 60;

  // Format back to original format
  const displayHours = newHours === 0 ? 12 : newHours > 12 ? newHours - 12 : newHours;
  const ampm = newHours >= 12 ? 'PM' : 'AM';

  const result = `${displayHours}:${newMinutes.toString().padStart(2, '0')} ${ampm}`;
  console.log('🔧 Rounding result:', result);
  return result;
};

// Apply timestamp rounding based on company settings
export const applyTimestampRounding = async (timestamp, companyId) => {
  try {
    console.log('🔍 Timestamp rounding check - Company ID:', companyId, 'Timestamp:', timestamp);

    // If no company ID provided, no rounding
    if (!companyId) {
      console.log('🔍 No company ID - no timestamp rounding applied');
      return timestamp;
    }

    // TEMPORARY: Always enable rounding for testing company-1757441106691
    if (companyId === 'company-1757441106691') {
      console.log('🔍 TEMPORARY: Force enabling timestamp rounding for test company');
      const rounded = roundTimestampToQuarterHour(timestamp);
      console.log('🔍 Rounded timestamp result:', rounded);
      return rounded;
    }

    // Get company settings
    const settings = await getCompanySettings(companyId);
    console.log('🔍 Full company settings:', settings);
    console.log('🔍 Time rounding settings:', settings?.timeRounding);

    // Check if time rounding is enabled
    if (!settings?.timeRounding?.enabled) {
      console.log('🔍 Timestamp rounding not enabled');
      return timestamp;
    }

    // Apply 15-minute rounding to timestamp
    console.log('🔍 Applying timestamp rounding to:', timestamp);
    const rounded = roundTimestampToQuarterHour(timestamp);
    console.log('🔍 Rounded timestamp result:', rounded);
    return rounded;
  } catch (error) {
    console.error('Error applying timestamp rounding:', error);
    // Return original timestamp if there's an error
    return timestamp;
  }
};

// Apply time rounding based on company settings
export const applyTimeRounding = async (timeString, companyId) => {
  try {
    console.log('🔍 Time rounding check - Company ID:', companyId);

    // If no company ID provided, no rounding
    if (!companyId) {
      console.log('🔍 No company ID - no rounding applied');
      return timeString;
    }

    // TEMPORARY: Always enable rounding for testing company-1757441106691
    if (companyId === 'company-1757441106691') {
      console.log('🔍 TEMPORARY: Force enabling rounding for test company');
      const rounded = roundToQuarterHour(timeString);
      console.log('🔍 Rounded result:', rounded);
      return rounded;
    }

    // Get company settings
    const settings = await getCompanySettings(companyId);
    console.log('🔍 Full company settings:', settings);
    console.log('🔍 Time rounding settings:', settings?.timeRounding);

    // Check if time rounding is enabled
    if (!settings?.timeRounding?.enabled) {
      console.log('🔍 Time rounding not enabled');
      return timeString;
    }

    // Apply 15-minute rounding
    console.log('🔍 Applying rounding to:', timeString);
    const rounded = roundToQuarterHour(timeString);
    console.log('🔍 Rounded result:', rounded);
    return rounded;
  } catch (error) {
    console.error('Error applying time rounding:', error);
    // Return original time if there's an error
    return timeString;
  }
};

// Get time rounding settings display text
export const getTimeRoundingDisplay = (settings) => {
  if (!settings?.timeRounding?.enabled) {
    return null;
  }

  return 'Times rounded to nearest 15 minutes';
};

// Enable time rounding for a company (utility function)
export const enableTimeRoundingForCompany = async (companyId) => {
  try {
    console.log('🔧 Enabling time rounding for company:', companyId);

    const settings = await getCompanySettings(companyId);
    const updatedSettings = {
      ...settings,
      timeRounding: {
        enabled: true,
        roundingInterval: 15,
        effectiveDate: new Date().toISOString()
      }
    };

    await updateCompanySettings(companyId, updatedSettings);
    console.log('🔧 Time rounding enabled successfully');
    return true;
  } catch (error) {
    console.error('🔧 Error enabling time rounding:', error);
    return false;
  }
};